import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, of, Observable, from, combineLatest } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ApiService } from '../../api/api.service';
import { ProjectTemplates, ProjectTemplate } from './project-template.interface';
import { ProjectModule } from '../module/project-module.interface';
import { TemplateService } from '../../utilities/template/template.service';
import { ProjectElement } from '../element/project-element.interface';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectTemplateCoreService {

  /** Project templates subject. */
  protected _projectTemplates: Subject<ProjectTemplates> = new BehaviorSubject<ProjectTemplates>(null);

  /** Project templates observable. */
  public projectTemplate$ = this._projectTemplates.asObservable();

  /** Project templates modules. */
  protected _projectTemplateElements: Subject<ProjectElement[]> = new BehaviorSubject<ProjectElement[]>(null);

  /** Project templates modules observable. */
  public projectTemplateElement$ = this._projectTemplateElements.asObservable();

  /** Project templates modules. */
  protected _projectTemplateModules: Subject<ProjectModule[]> = new BehaviorSubject<ProjectModule[]>(null);

  /** Project templates modules observable. */
  public projectTemplateModule$ = this._projectTemplateModules.asObservable();

  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected templateService: TemplateService
  ) { }

  /**
   * Get templates.
   *
   * @returns Project templates.
   */
  public get(): ProjectTemplates {
    let templates: ProjectTemplates;
    this._projectTemplates.pipe(take(1)).subscribe(t => templates = t);
    return templates;
  }

  /**
   * Get templates.
   *
   * @param id Template id.
   * @returns Project template.
   */
  public getTemplate(id: ProjectTemplate['_id']): ProjectTemplate {
    let template: ProjectTemplate;
    this._projectTemplates.pipe(take(1)).subscribe(ts => template = ts.find(t => t._id === id));
    return template;
  }

  /**
   * Get template elements.
   *
   * @returns Project elements.
   */
  public getElements(): ProjectElement[] {
    let elements: ProjectElement[];
    this._projectTemplateElements.pipe(take(1)).subscribe(e => elements = e);
    return elements;
  }

  /**
   * Get template modules.
   *
   * @returns Project modules.
   */
  public getModules(): ProjectModule[] {
    let modules: ProjectModule[];
    this._projectTemplateModules.pipe(take(1)).subscribe(m => modules = m);
    return modules;
  }

  /**
   * Build template tree with elements.
   *
   * By default, the template does only contain an *array of element id's*,
   * use this method to populate the modules with the correct elements.
   *
   * @param id Template id to build.
   * @returns Template with correct elements.
   */
  public getTemplateTree(id: ProjectTemplate['_id']): ProjectModule[] {
    return this.templateService.populateTreeWithElements(
      this.getTemplate(id),
      this.getModules(),
      this.getElements()
    );
  }

  /**
   * Initialize templates.
   *
   * Resolver function with legacy support for the AngularJS router.
   * For the best results, and easy migration later on, the data is
   * requested from the API, saved here in the datastore, and a fake
   * Promise response is returned to the legacy router. Because of
   * this, the legacy router will wait for the datastore to complete.
   *
   * The API is only called *once*. When the data is already available
   * locally, the cached templates are returned.
   *
   * @note Because it will return only cached data, there may be old
   *  data after updating the templates via the template routes. This
   *  store does not sync the data, because the legacy template pages
   *  still only use $scope. If so, add a clear() function which will
   *  be called when switching between project and template pages.
   *
   * @todo After migration, remove the Promise, and move this method
   *  to proper Angular Router Resolver.
   *
   * @returns Promise when the data is available. Empty response.
   */
  public resolve(): Promise<boolean> {
    return new Promise(resolve => {
      const templates = this.get();
      if (templates) {
        resolve();
      } else {
        combineLatest([
          this.apiLegacyService.getProjectTemplates(),
          this.apiLegacyService.getTemplateModules(),
          this.apiLegacyService.getTemplateElements(1)
        ]).subscribe(([buildingTemplates, modules, elements]: [ProjectTemplates, ProjectModule[], ProjectElement[]]) => {
          this._projectTemplates.next(buildingTemplates);
          this._projectTemplateModules.next(modules);
          this._projectTemplateElements.next(elements);
          resolve(true);
        });
      }
    });
  }
}
